import { useEffect, useState } from 'react';
import { Plan, PlanProductEntry } from 'screens/Plans/ConvertPlanModalV2/types';
import { Product } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import useMapProducts from 'screens/Applications&WorkOrders/Components/hooks/useMapProducts';
import { transformPlanProductEntryToProduct } from '../helpers';

interface UseProductsFromPlanParams {
  selectedProducts: PlanProductEntry[] | undefined;
  plan: Plan | undefined;
}

interface UseProductsFromPlanReturn {
  mappedProducts: any[];
  isMappingProducts: boolean;
}

const useProductsFromPlan = ({
  selectedProducts,
  plan
}: UseProductsFromPlanParams): UseProductsFromPlanReturn => {
  const [mappedProducts, setMappedProducts] = useState<any[]>([]);
  const [isMappingProducts, setIsMappingProducts] = useState(false);

  const { buildProductsDataMap, fetchAllPests, mapProducts } = useMapProducts();

  useEffect(() => {
    if (!selectedProducts?.length) {
      setMappedProducts([]);
      return;
    }

    const mapProductsData = async () => {
      setIsMappingProducts(true);
      try {
        // Transform PlanProductEntry to Product format
        const products: Product[] = selectedProducts.map(
          transformPlanProductEntryToProduct
        );

        // Build products data map and fetch pests
        const productsFullDataMap = await buildProductsDataMap(products);
        const allPests = await fetchAllPests(products);

        // Calculate total applied area from plan
        const totalAppliedArea =
          plan?.cropZones?.reduce((sum, cz) => sum + (cz.areaValue || 0), 0) ||
          0;

        // Map products to form values format
        const mapped = mapProducts(
          products,
          productsFullDataMap,
          allPests,
          totalAppliedArea
        );

        // Unlike Recommendation/Invoice conversion, a Plan product's $0 cost
        // must carry over as $0, not auto-fill from the catalog list price —
        // the user can still do that manually via "Reset to List Price" in
        // the destination wizard's Product Cost section.
        setMappedProducts(mapped);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error mapping products from plan:', error);
        setMappedProducts([]);
      } finally {
        setIsMappingProducts(false);
      }
    };

    mapProductsData();
  }, [
    selectedProducts,
    plan,
    buildProductsDataMap,
    fetchAllPests,
    mapProducts
  ]);

  return {
    mappedProducts,
    isMappingProducts
  };
};

export default useProductsFromPlan;
